<template>
    <portal to="modals" :disabled="anchored">
        <transition
                name="sheet"
                @after-leave="leave()"
                @after-enter="enter()"
                :duration="animationDuration"
        >
            <div
                    v-if="visible"
                    class="h-full w-full overflow-hidden"
                    :class="{'fixed': !anchored, 'absolute': anchored}"
                    :style="cssVars"
            >
                <div
                        v-if="overlay"
                        class="sheet-mask w-full h-full top-0 bg-black/70"
                        :class="{'fixed': !anchored, 'absolute': anchored}"
                        @click="onOverlayClick()"
                        :style="{ 'z-index': z }"
                ></div>

                <div
                        class="sheet-container w-full bg-backdrop rounded-t-xl pt-4 max-h-screen overflow-auto"
                        @click.stop
                        :class="{'fixed': !anchored, 'absolute': anchored }"
                        :style="{ 'z-index': z + 1, 'bottom': offset }"
                >
                    <div class="safe-area-bottom">
                        <div class="px-4 flex justify-end sticky top-0 right-0 z-50" v-if="!persistent">
                            <a href="#" @click.prevent="close()" class="rounded-full">
                                <img src="/src/assets/icons/close-circle.svg" alt="Close"/>
                            </a>
                        </div>

                        <slot/>
                    </div>
                </div>
            </div>
        </transition>
    </portal>
</template>

<script>
export default {
  name: 'BottomSheet',
  emits: ['show', 'hide'],
  props: {
    z: {
      type: Number,
      default: 50
    },
    anchored: {
      type: Boolean,
      default: false
    },
    overlay: {
      type: Boolean,
      default: true
    },
    offset: {
      type: String,
      default: '0'
    },
    animationDuration: {
        type: Number,
        default: 500
    },
    persistent: {
      type: Boolean,
      default: false
    }
  },
  data () {
    return {
      visible: false,
      opening: null,
      closing: null
    }
  },
  computed: {
    cssVars () {
      return {
        '--duration': `${this.animationDuration}ms`,
        'z-index': this.z
      }
    }
  },
  methods: {
    onOverlayClick () {
      if (!this.persistent) {
        this.close()
      }
    },
    close () {
      return new Promise((resolve) => {
        this.closing = resolve
        this.visible = false
      })
    },
    open () {
      return new Promise((resolve) => {
        this.opening = resolve
        this.visible = true
      })
    },
    toggle () {
      if (this.visible) {
        return this.close()
      } else {
        return this.open()
      }
    },
    enter () {
      if (this.opening) this.opening()
      this.$emit('show')
    },
    leave () {
      if (this.closing) this.closing()
      this.$emit('hide')
    },
    onKeyDown (e) {
      if (this.value && e.keyCode === 27) {
        this.close()
      }
    }
  },
  mounted () {
    document.addEventListener('keydown', this.onKeyDown)
  },
  unmounted () {
    document.removeEventListener('keydown', this.onKeyDown)
  }
}
</script>

<style scoped lang="scss">
$duration: var(--duration);
$off-screen-offset: 50px;

.sheet-enter .sheet-mask,
.sheet-leave-to .sheet-mask {
  opacity: 0;
}

.sheet-enter-active .sheet-mask,
.sheet-leave-active .sheet-mask {
  transition: opacity $duration ease-in;
}

.sheet-container {
  .sheet-enter &,
  .sheet-leave-to & {
    transform: translateY(100%);
  }

  .sheet-enter-active &,
  .sheet-leave-active & {
    transition: transform $duration ease-in;
  }
}

.sheet-container {
  transform: translateY($off-screen-offset);
  padding-bottom: $off-screen-offset;
}

.sheet-enter-active .sheet-container {
  animation: bounce-in $duration;
}

.sheet-leave-active .sheet-container {
  animation: bounce-in $duration reverse;
}

@keyframes bounce-in {
  0% {
    transform: translateY(100%);
  }
  70% {
    transform: translateY(0);
  }
  100% {
    transform: translateY($off-screen-offset);
  }
}
</style>
